import { ResultOf } from ':src/graphql';
import { parse } from 'json2csv';
import { sessionsForCSVExportQuery } from '../queries';

type SessionsQueryResult = ResultOf<typeof sessionsForCSVExportQuery>;

type SessionFieldType = NonNullable<SessionsQueryResult['sessions']>['result'][number];

interface SessionCSVRow {
  sessionId: number;
  date: string;
  duration: string;
  sessionType: string;
  supervisor: string;
  superviseeIds: string;
  superviseeNames: string;
  superviseeEmails: string;
}

export default function generateSessionsCSV(
  sessions: NonNullable<SessionsQueryResult['sessions']>['result'],
  noShowOnly = false,
): string {
  const rows: SessionCSVRow[] = [];

  sessions.forEach((session: SessionFieldType) => {
    const sessionDate = new Date(session.scheduledAt).toLocaleString();
    const sessionType = session.sessionType.displayName;
    const supervisorName = session.supervisor.legalFullName;
    const superviseeInfo = session.sessionAttendees
      .map((supervisee) => {
        if (noShowOnly && supervisee.attended) {
          return null;
        }

        const programName = supervisee.user.organizationUser?.organizationProgram?.name;
        const name = supervisee.user.legalFullName;
        const nameWithProgram = programName ? `${name} (${programName})` : name;
        const email = supervisee.user.email || '';
        const { id } = supervisee.user;

        return { nameWithProgram, email, id };
      })
      .filter((entry) => entry !== null);

    const superviseeNames = superviseeInfo
      .map((supervisee) => supervisee.nameWithProgram)
      .join('\n');
    const superviseeEmails = superviseeInfo.map((supervisee) => supervisee.email).join('\n');
    const superviseeIds = superviseeInfo.map((supervisee) => supervisee.id).join('\n');

    rows.push({
      sessionId: session.id,
      date: sessionDate,
      duration: session.duration.toString(),
      sessionType,
      supervisor: supervisorName,
      superviseeNames,
      superviseeEmails,
      superviseeIds,
    });
  });

  const fields = [
    { label: 'Session Id', value: 'sessionId' },
    { label: 'Date', value: 'date' },
    { label: 'Duration', value: 'duration' },
    { label: 'Type', value: 'sessionType' },
    { label: 'Supervisor', value: 'supervisor' },
    { label: 'Supervisee Names', value: 'superviseeNames' },
    { label: 'Supervisee Emails', value: 'superviseeEmails' },
    { label: 'Supervisee Ids', value: 'superviseeIds' },
  ];

  return parse(rows, { fields });
}
